import { useEffect, useState } from 'react';
import { useIsMobile } from '../ui/useMediaQuery';
import {
  NOTE_COLORS,
  type Item,
  type NoteItem,
  type TitleItem,
  type ImageItem,
  type ShapeItem,
  type StrokeItem,
  type RichTextItem,
  type LinkItem,
  type ShapeKind,
  type FileItem,
  type FrameItem,
  type TableItem,
  type TodoItem,
} from '../doc/schema';
import { openAsset } from './assets/localAssets';
import { formatBytes } from './items/FileItem';
import { ColorPicker, SizePicker } from '../ui/ColorPicker';
import { TextStyleFields } from './text/TextStyleFields';
import { textStyleOf, textStylePatch } from './text/textStyle';
import { refreshLinkPreview } from './actions';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../doc/zorder';
import {
  IconAlignB,
  IconAlignCX,
  IconAlignCY,
  IconAlignL,
  IconAlignR,
  IconAlignT,
  IconBack,
  IconBackward,
  IconDistH,
  IconDistV,
  IconDuplicate,
  IconExternal,
  IconForward,
  IconFront,
  IconGrid,
  IconGroup,
  IconLock,
  IconTrash,
  IconUngroup,
  IconUnlock,
} from '../ui/icons';
import {
  alignSelection,
  arrangeGrid,
  distributeSelection,
  groupSelection,
  normalizeSelection,
  toggleLockSelection,
  ungroupSelection,
  type AlignMode,
} from './arrange';
import { deleteSelection, duplicateSelection } from './actions';
import { useBoard, useItemsVersion, usePane, usePaneStore, useYMap } from './context';
import type { LayerDef } from '../doc/schema';

/** Properties panel for the current selection. Re-renders on document changes, which is fine for a panel. */
export function Inspector() {
  const board = useBoard();
  const pane = usePaneStore();
  const selection = usePane((s) => s.selection);
  useItemsVersion();
  const layers = useYMap<LayerDef>(board.layers);
  const mobile = useIsMobile();
  // On phones the inspector is a sheet above the toolbar, collapsed to its header until tapped.
  const [open, setOpen] = useState(false);

  const items: Item[] = [];
  for (const id of selection) {
    const item = board.index.get(id);
    if (item) items.push(item);
  }
  if (!items.length) return null;
  const single = items.length === 1 ? items[0] : undefined;
  const ids = new Set(items.map((i) => i.id));
  const ctx = { board, pane };
  const allLocked = items.every((i) => i.locked);
  const layerIds = new Set(items.map((i) => (i.layerId && layers[i.layerId] ? i.layerId : '')));
  const layerValue = layerIds.size === 1 ? [...layerIds][0]! : '__mixed__';
  const anyGrouped = items.some((i) => i.groupId);

  const collapsed = mobile && !open;

  return (
    <aside className={`inspector${collapsed ? ' is-collapsed' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      <div className="insp-head" onClick={mobile ? () => setOpen((o) => !o) : undefined} role={mobile ? 'button' : undefined}>
        <span>{single ? single.type : `${items.length} items`}</span>
        {mobile && (
          <span className="insp-quick" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-icon" title="Duplicate" aria-label="Duplicate" onClick={() => void duplicateSelection(ctx)}>
              <IconDuplicate />
            </button>
            <button type="button" className="btn btn-icon danger" title="Delete" aria-label="Delete" disabled={allLocked} onClick={() => deleteSelection(ctx)}>
              <IconTrash />
            </button>
            <span className="insp-caret" aria-hidden onClick={() => setOpen((o) => !o)}>
              {open ? '▾' : '▴'}
            </span>
          </span>
        )}
      </div>
      {!collapsed && (
      <div className="insp-body">
        {single && <GeometryFields item={single} />}
        {single?.type === 'note' && <NoteFields item={single} />}
        {single?.type === 'title' && <TitleFields item={single} />}
        {single?.type === 'image' && <ImageFields item={single} />}
        {single?.type === 'shape' && <ShapeFields item={single} />}
        {single?.type === 'stroke' && <StrokeFields item={single} />}
        {(single?.type === 'richtext' || single?.type === 'table' || single?.type === 'todo') && <RichTextFields item={single} />}
        {single?.type === 'file' && <FileFields item={single} />}
        {single?.type === 'link' && <LinkFields item={single} />}
        {single?.type === 'frame' && <FrameFields item={single} />}

        {items.length >= 2 && (
          <>
            <div className="insp-label">Arrange</div>
            <div className="insp-grid">
              {(
                [
                  ['left', IconAlignL, 'Align left edges'],
                  ['centerX', IconAlignCX, 'Align horizontal centres'],
                  ['right', IconAlignR, 'Align right edges'],
                  ['top', IconAlignT, 'Align top edges'],
                  ['centerY', IconAlignCY, 'Align vertical centres'],
                  ['bottom', IconAlignB, 'Align bottom edges'],
                ] as [AlignMode, () => React.JSX.Element, string][]
              ).map(([mode, Icon, title]) => (
                <button key={mode} type="button" className="btn" title={title} onClick={() => alignSelection(ctx, mode)}>
                  <Icon />
                </button>
              ))}
            </div>
            <div className="btn-row">
              <button type="button" className="btn" title="Distribute horizontally (3+)" disabled={items.length < 3} onClick={() => distributeSelection(ctx, 'x')}>
                <IconDistH />
              </button>
              <button type="button" className="btn" title="Distribute vertically (3+)" disabled={items.length < 3} onClick={() => distributeSelection(ctx, 'y')}>
                <IconDistV />
              </button>
              <button type="button" className="btn" title="Arrange in a grid" onClick={() => arrangeGrid(ctx)}>
                <IconGrid />
              </button>
            </div>
            <div className="insp-label">Same size as first selected</div>
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'width')}>
                Width
              </button>
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'height')}>
                Height
              </button>
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'longest')}>
                Fit
              </button>
            </div>
          </>
        )}

        {Object.keys(layers).length > 0 && (
          <>
            <div className="insp-label">Layer</div>
            <select
              className="font-select"
              value={layerValue}
              onChange={(e) => board.moveSelectionToLayer(e.target.value === '' ? null : e.target.value, [...ids])}
            >
              {layerValue === '__mixed__' && <option value="__mixed__">(mixed)</option>}
              <option value="">Default</option>
              {Object.entries(layers)
                .sort((a, b) => b[1].order - a[1].order)
                .map(([id, l]) => (
                  <option key={id} value={id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </>
        )}

        <div className="insp-label">Order</div>
        <div className="btn-row">
          <button type="button" className="btn" title="Bring to front (Ctrl+])" onClick={() => bringToFront(board, ids)}>
            <IconFront />
          </button>
          <button type="button" className="btn" title="Bring forward (])" onClick={() => bringForward(board, ids)}>
            <IconForward />
          </button>
          <button type="button" className="btn" title="Send backward ([)" onClick={() => sendBackward(board, ids)}>
            <IconBackward />
          </button>
          <button type="button" className="btn" title="Send to back (Ctrl+[)" onClick={() => sendToBack(board, ids)}>
            <IconBack />
          </button>
        </div>

        <div className="btn-row">
          {items.length >= 2 && (
            <button type="button" className="btn" title="Group (Ctrl+G)" onClick={() => groupSelection(ctx)}>
              <IconGroup /> Group
            </button>
          )}
          {anyGrouped && (
            <button type="button" className="btn" title="Ungroup (Ctrl+Shift+G)" onClick={() => ungroupSelection(ctx)}>
              <IconUngroup /> Ungroup
            </button>
          )}
          <button type="button" className={`btn${allLocked ? ' is-active' : ''}`} title="Lock / unlock (Ctrl+L)" onClick={() => toggleLockSelection(ctx)}>
            {allLocked ? <IconUnlock /> : <IconLock />} {allLocked ? 'Unlock' : 'Lock'}
          </button>
        </div>
        <div className="btn-row">
          <button type="button" className="btn" title="Duplicate (Ctrl+D)" onClick={() => void duplicateSelection(ctx)}>
            <IconDuplicate /> Duplicate
          </button>
          <button type="button" className="btn danger" title="Delete (Del)" disabled={allLocked} onClick={() => deleteSelection(ctx)}>
            <IconTrash /> Delete
          </button>
        </div>
      </div>
      )}
    </aside>
  );
}

function GeometryFields({ item }: { item: Item }) {
  const board = useBoard();
  const set = (key: 'x' | 'y' | 'w' | 'h' | 'rotation') => (v: number) => board.updateItem(item.id, { [key]: v });
  return (
    <>
      <div className="insp-row">
        <NumberField label="X" value={item.x} onCommit={set('x')} />
        <NumberField label="Y" value={item.y} onCommit={set('y')} />
      </div>
      <div className="insp-row">
        <NumberField label="W" value={item.w} min={1} onCommit={set('w')} />
        <NumberField label="H" value={item.h} min={1} onCommit={set('h')} />
      </div>
      <div className="insp-row">
        <NumberField label="R" value={item.rotation} step={1} onCommit={set('rotation')} />
      </div>
    </>
  );
}

function NoteFields({ item }: { item: NoteItem }) {
  const board = useBoard();
  return (
    <>
      <div className="insp-label">Paper</div>
      <div className="swatches">
        {NOTE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`swatch note-${c}${item.props.color === c ? ' is-active' : ''}`}
            title={c}
            aria-label={c}
            onClick={() => board.updateItem(item.id, { props: { color: c } })}
          />
        ))}
      </div>
      <TextStyleFields
        value={textStyleOf(item)}
        onChange={(patch) => board.updateItem(item.id, { props: textStylePatch('note', patch) })}
        defaultColorLabel="ink"
      />
    </>
  );
}

function TitleFields({ item }: { item: TitleItem }) {
  const board = useBoard();
  return (
    <TextStyleFields
      value={textStyleOf(item)}
      onChange={(patch) => board.updateItem(item.id, { props: textStylePatch('title', patch) })}
    />
  );
}

function ImageFields({ item }: { item: ImageItem }) {
  const board = useBoard();
  const resetAspect = () => {
    const ratio = item.props.naturalW / Math.max(1, item.props.naturalH);
    board.updateItem(item.id, { h: Math.round(item.w / ratio) });
  };
  return (
    <>
      <div className="insp-label">Opacity</div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={item.props.opacity}
        onChange={(e) => board.updateItem(item.id, { props: { opacity: Number(e.target.value) } })}
      />
      <div className="btn-row">
        <button type="button" className="btn" onClick={resetAspect}>
          Reset aspect
        </button>
      </div>
      <div className="insp-muted">
        {item.props.naturalW} × {item.props.naturalH} px
      </div>
    </>
  );
}

function ShapeFields({ item }: { item: ShapeItem }) {
  const board = useBoard();
  const p = item.props;
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  const isLine = p.kind === 'line' || p.kind === 'arrow';
  return (
    <>
      <div className="btn-row">
        {(['rect', 'ellipse', 'line', 'arrow'] as ShapeKind[]).map((k) => (
          <button key={k} type="button" className={`btn${p.kind === k ? ' is-active' : ''}`} onClick={() => set({ kind: k })}>
            {k}
          </button>
        ))}
      </div>
      {!isLine && (
        <>
          <div className="insp-label">Fill</div>
          <ColorPicker value={p.fill} onChange={(fill) => set({ fill })} allowNone />
        </>
      )}
      <div className="insp-label">Stroke</div>
      <ColorPicker value={p.stroke} onChange={(stroke) => set({ stroke })} allowNone={!isLine} />
      <SizePicker value={p.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => set({ strokeWidth })} />
      {isLine && (
        <button type="button" className="btn" onClick={() => set({ flip: !p.flip })}>
          Flip direction
        </button>
      )}
    </>
  );
}

function FrameFields({ item }: { item: FrameItem }) {
  const board = useBoard();
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  return (
    <>
      <div className="insp-label">Title</div>
      <input
        className="insp-input"
        value={item.props.title}
        placeholder="Frame"
        onChange={(e) => set({ title: e.target.value })}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div className="insp-label">Colour</div>
      <ColorPicker value={item.props.color ?? 'none'} onChange={(c) => set({ color: c === 'none' ? null : c })} allowNone />
      <div className="insp-muted">Items whose centre is inside the frame move with it.</div>
    </>
  );
}

function StrokeFields({ item }: { item: StrokeItem }) {
  const board = useBoard();
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  return (
    <>
      <div className="insp-label">Colour</div>
      <ColorPicker value={item.props.color} onChange={(color) => set({ color })} />
      <div className="insp-label">Size</div>
      <SizePicker value={item.props.size} options={[2, 4, 8, 14, 24, 40]} onChange={(size) => set({ size })} />
      <div className="btn-row">
        {(['pen', 'highlighter'] as const).map((t) => (
          <button key={t} type="button" className={`btn${item.props.tool === t ? ' is-active' : ''}`} onClick={() => set({ tool: t })}>
            {t}
          </button>
        ))}
      </div>
    </>
  );
}

function RichTextFields({ item }: { item: RichTextItem | TableItem | TodoItem }) {
  const board = useBoard();
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  return (
    <>
      <div className="insp-label">Background</div>
      <ColorPicker value={item.props.bg ?? 'none'} onChange={(bg) => set({ bg: bg === 'none' ? null : bg })} allowNone />
      <TextStyleFields
        value={textStyleOf(item)}
        onChange={(patch) => set(textStylePatch('richtext', patch))}
        showBoldItalic={false}
      />
      <div className="insp-muted">Select text while editing for inline formatting.</div>
    </>
  );
}

function FileFields({ item }: { item: FileItem }) {
  return (
    <>
      <div className="insp-label">Attachment</div>
      <div className="insp-url">
        {item.props.name} · {formatBytes(item.props.size)}
      </div>
      <div className="btn-row">
        <button type="button" className="btn" onClick={() => void openAsset(item.props.assetId, item.props.name)}>
          <IconExternal /> Download
        </button>
      </div>
    </>
  );
}

function LinkFields({ item }: { item: LinkItem }) {
  const board = useBoard();
  return (
    <>
      <div className="insp-label">URL</div>
      <div className="insp-url" title={item.props.url}>
        {item.props.url}
      </div>
      <div className="btn-row">
        <a className="btn" href={item.props.url} target="_blank" rel="noopener noreferrer">
          <IconExternal /> Open
        </a>
        <button type="button" className="btn" onClick={() => void refreshLinkPreview(board, item.id)}>
          Refresh preview
        </button>
      </div>
    </>
  );
}

function NumberField({
  label,
  value,
  onCommit,
  min,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onCommit: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  const [text, setText] = useState(String(Math.round(value * 100) / 100));
  useEffect(() => setText(String(Math.round(value * 100) / 100)), [value]);
  const commit = () => {
    const n = Number(text);
    if (!Number.isFinite(n)) return setText(String(value));
    const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        value={text}
        step={step}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          e.stopPropagation();
        }}
      />
    </label>
  );
}
